import { useMemo } from "react";
import { priceTier, TIER_META, type Tier } from "../lib/pricing";
import type { TicketGroup } from "../types/api";
import styles from "./VenueMap.module.css";

type Props = {
  groups: TicketGroup[];
  selectedSection: string | null;
  hoveredSection: string | null;
  onSelectSection: (s: string | null) => void;
  onHoverSection: (s: string | null) => void;
};

type SectionInfo = {
  name: string;
  minPrice: number;
  qty: number;
  tier: Tier;
};

const TIERS: Tier[] = [0, 1, 2, 3];

export default function VenueMap({
  groups,
  selectedSection,
  hoveredSection,
  onSelectSection,
  onHoverSection,
}: Props) {
  // Aggregate ticket groups into one entry per section, with min price + tier.
  const sections = useMemo<SectionInfo[]>(() => {
    const allPrices = groups.map((g) => g.pricePerTicket);
    const bySection = new Map<
      string,
      { minPrice: number; qty: number }
    >();
    for (const g of groups) {
      const cur = bySection.get(g.section);
      if (cur) {
        cur.qty += g.quantityAvailable;
        if (g.pricePerTicket < cur.minPrice) cur.minPrice = g.pricePerTicket;
      } else {
        bySection.set(g.section, {
          minPrice: g.pricePerTicket,
          qty: g.quantityAvailable,
        });
      }
    }
    return Array.from(bySection, ([name, info]) => ({
      name,
      minPrice: info.minPrice,
      qty: info.qty,
      tier: priceTier(info.minPrice, allPrices),
    })).sort((a, b) => a.minPrice - b.minPrice);
  }, [groups]);

  const byTier = useMemo(() => {
    const out: Record<Tier, SectionInfo[]> = { 0: [], 1: [], 2: [], 3: [] };
    for (const s of sections) out[s.tier].push(s);
    return out;
  }, [sections]);

  return (
    <div className={styles.map}>
      <div className={styles.header}>
        <span className={styles.title}>Seat map</span>
        {selectedSection && (
          <button
            type="button"
            className={styles.clearBtn}
            onClick={() => onSelectSection(null)}
          >
            Clear filter
          </button>
        )}
      </div>

      <div className={styles.canvas}>
        {/* Render tiers from cheapest (top, farthest) to premium (bottom, closest) */}
        {[...TIERS].map((tier) => {
          const list = byTier[tier];
          if (list.length === 0) return null;
          return (
            <div key={tier} className={styles.tierRow}>
              {list.map((s) => {
                const isActive = selectedSection === s.name;
                const isHovered = hoveredSection === s.name;
                const dim =
                  (selectedSection && !isActive) ||
                  (!selectedSection && hoveredSection && !isHovered);
                return (
                  <button
                    key={s.name}
                    type="button"
                    className={`${styles.section} ${isActive ? styles.active : ""} ${dim ? styles.dim : ""}`}
                    style={{ background: TIER_META[tier].gradient }}
                    onClick={() => onSelectSection(isActive ? null : s.name)}
                    onMouseEnter={() => onHoverSection(s.name)}
                    onMouseLeave={() => onHoverSection(null)}
                    aria-pressed={isActive}
                    aria-label={`${s.name}, from $${Math.round(s.minPrice)}, ${s.qty} tickets available`}
                  >
                    <span className={styles.sectionName}>{s.name}</span>
                    <span className={styles.sectionPrice}>
                      ${Math.round(s.minPrice)}
                    </span>
                  </button>
                );
              })}
            </div>
          );
        })}

        <div className={styles.stage}>
          <span className={styles.stageLabel}>STAGE</span>
        </div>
      </div>

      <div className={styles.legend}>
        {TIERS.map((tier) => {
          const meta = TIER_META[tier];
          const has = byTier[tier].length > 0;
          return (
            <div
              key={tier}
              className={`${styles.legendItem} ${!has ? styles.legendDim : ""}`}
            >
              <span
                className={styles.legendDot}
                style={{ background: meta.gradient }}
              />
              <span>{meta.label}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
